import React, { useEffect, useRef, useState } from 'react';
import { FormContainer } from 'react-hook-form-mui';

import { useSelectUserTimezone } from '@treneo/features/user';
import type { DateFormat, TimeFormat } from '@treneo/shared/constants';
import { DEFAULT_TIMEZONE, FORM_MODES } from '@treneo/shared/constants';
import { useDateTimeConfig } from '@treneo/shared/contexts';
import {
  AutocompleteField,
  FormButtons,
  TextField as FormTextField,
  useAppForm,
  useAppWatch
} from '@treneo/shared/forms';
import { useAppTranslations } from '@treneo/shared/i18n';
import { logger } from '@treneo/shared/logger';
import { Colors } from '@treneo/shared/themes';
import {
  Box,
  ConfirmationDialog,
  DateInfo,
  TimeInfo,
  toast,
  Typography,
  useConfirmationDialog
} from '@treneo/shared/ui';

import type { CourseDateError } from '../../../utils';
import {
  convertCourseDatesFormat,
  createNewCourseDateEntry,
  validateAllCourseDates
} from '../../../utils';

import { CheckboxOptionsSection } from './components/checkbox-section';
import { CourseDateItem } from './components/course-date-item';
import { ErrorDisplay } from './components/error-display';
import { LoadingSkeleton } from './components/loading-skeleton';
import { ParticipantsSection } from './components/participants-section';
import {
  TrainerSelectSection,
  TrainerSelectSectionWithQuery
} from './components/trainer-select-section';
import { useCourseDates } from './hooks/use-course-dates';
import { useCourseFormOptions } from './hooks/use-course-form-options';
import { courseFormFieldNames } from './course-form-field-names';
import {
  getDefaultFormValues,
  mapDatacenterOptions,
  mapOrganizationOptions,
  mapTimezoneOptions
} from './form-options-utils';
import { createCourseFormSchema } from './schema';
import { FormContainer as StyledFormContainer } from './styled';
import type { CourseDateEntry, CourseFormData, CourseFormProps } from './types';
import { validateCourseFormData } from './validation';

const DEFAULT_COURSE_START_TIME = '09:00';
const DEFAULT_COURSE_END_TIME = '16:00';

export const CourseForm = ({
  mode,
  onSubmit,
  onCancel,
  isSubmitSuccess,
  isLoading,
  initialData,
  metadata,
  courseId,
  formOptions: formOptionsProp,
  isLoadingOptions: isLoadingOptionsProp,
  optionsError: optionsErrorProp,
  organizationIdForTrainers,
  organizationTrainers,
  organizationTrainersLoading,
  assignedTrainerFromCourse
}: Readonly<CourseFormProps>) => {
  const { t } = useAppTranslations();
  const userTimezone = useSelectUserTimezone();
  const { dateFormat, timeFormat } = useDateTimeConfig();
  const {
    openConfirm,
    closeConfirm,
    isOpen: isConfirmOpen,
    content: confirmContent,
    handleConfirm: handleConfirmYes
  } = useConfirmationDialog();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<Partial<CourseFormData> | null>(null);
  const [formValidationErrors, setFormValidationErrors] = useState<Record<string, string>>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [courseDates, setCourseDates] = useState<CourseDateEntry[]>([]);
  const [previousDateFormat, setPreviousDateFormat] = useState<DateFormat | null>(null);
  const [previousTimeFormat, setPreviousTimeFormat] = useState<TimeFormat | null>(null);
  const submitButtonRef = useRef<HTMLButtonElement | null>(null);
  const formContainerRef = useRef<HTMLDivElement | null>(null);
  const editDatesAppliedRef = useRef(false);
  const lastEditCourseIdRef = useRef<string | undefined>(undefined);
  const handleSubmitAttemptRef = useRef<() => void>(() => {});

  const hookFormOptions = useCourseFormOptions();
  const formOptions = formOptionsProp ?? hookFormOptions.formOptions;
  const isLoadingOptions = isLoadingOptionsProp ?? hookFormOptions.isLoadingOptions;
  const optionsError = optionsErrorProp ?? hookFormOptions.optionsError;
  const isBasicDataLoaded = !isLoadingOptions && !!formOptions;

  const canRenderForm =
    isBasicDataLoaded &&
    (mode === FORM_MODES.CREATE || (mode === FORM_MODES.EDIT && !!initialData));

  const defaultValues: Partial<CourseFormData> =
    !formOptions || (mode === FORM_MODES.EDIT && !initialData)
      ? {}
      : getDefaultFormValues(formOptions, mode, initialData, userTimezone);

  const hasDefaults = Object.keys(defaultValues).length > 0;
  const formContext = useAppForm<CourseFormData>(createCourseFormSchema(), {
    ...(hasDefaults && { defaultValues: defaultValues as CourseFormData })
  });

  const watchedTimezone = useAppWatch({
    control: formContext.control,
    name: courseFormFieldNames.timezone
  });
  const currentTimezone = watchedTimezone || DEFAULT_TIMEZONE;

  const {
    courseDateErrors,
    setCourseDateErrors,
    addCourseDate,
    removeCourseDate,
    updateCourseDateRange,
    validateDateRangeOnBlur,
    getCourseDateForDisplay
  } = useCourseDates({
    formContext,
    canRenderForm,
    courseDates,
    setCourseDates,
    defaultStartTime: DEFAULT_COURSE_START_TIME,
    defaultEndTime: DEFAULT_COURSE_END_TIME,
    currentTimezone,
    dateFormat: dateFormat as DateFormat,
    timeFormat: timeFormat as TimeFormat
  });

  useEffect(() => {
    if (mode !== FORM_MODES.EDIT || !initialData || !formOptions || courseId === undefined) {
      return;
    }
    if (lastEditCourseIdRef.current === courseId) {
      return;
    }
    lastEditCourseIdRef.current = courseId;
    editDatesAppliedRef.current = false;

    const formValues = getDefaultFormValues(
      formOptions,
      FORM_MODES.EDIT,
      initialData,
      userTimezone
    );
    formContext.reset(formValues);
    setCourseDates(formValues.courseDates ?? []);
    setOriginalFormData(formValues);
    setFormKey(prev => prev + 1);
    editDatesAppliedRef.current = true;
  }, [mode, initialData, formOptions, courseId, userTimezone, formContext]);

  useEffect(() => {
    const editDates = defaultValues?.courseDates;
    if (mode !== FORM_MODES.EDIT || !editDates?.length || editDatesAppliedRef.current) {
      return;
    }
    editDatesAppliedRef.current = true;
    setCourseDates(editDates);
  }, [mode, defaultValues?.courseDates]);

  useEffect(() => {
    if (mode !== FORM_MODES.CREATE || !isBasicDataLoaded || !formOptions || originalFormData) {
      return;
    }
    const values = getDefaultFormValues(formOptions, FORM_MODES.CREATE, undefined, userTimezone);
    const courseTimezone = values.timezone ?? DEFAULT_TIMEZONE;
    const oneDate = [
      createNewCourseDateEntry('', '', courseTimezone, undefined, {
        defaultStartTime: DEFAULT_COURSE_START_TIME,
        defaultEndTime: DEFAULT_COURSE_END_TIME,
        dateFormat: dateFormat as DateFormat,
        timeFormat: timeFormat as TimeFormat
      })
    ];
    const formValues = { ...values, courseDates: oneDate };
    formContext.reset(formValues);
    setCourseDates(oneDate);
    setOriginalFormData(formValues);
    setFormKey(prev => prev + 1);
  }, [
    isBasicDataLoaded,
    mode,
    formOptions,
    userTimezone,
    formContext,
    originalFormData,
    dateFormat,
    timeFormat
  ]);

  useEffect(() => {
    if (isSubmitSuccess) {
      setOriginalFormData(null);
      setIsSubmitting(false);
      setHasAttemptedSubmit(false);
      setFormValidationErrors({});
    }
  }, [isSubmitSuccess]);

  useEffect(() => {
    if (previousDateFormat === null && previousTimeFormat === null) {
      setPreviousDateFormat(dateFormat as DateFormat);
      setPreviousTimeFormat(timeFormat as TimeFormat);
    }
  }, [dateFormat, timeFormat, previousDateFormat, previousTimeFormat]);

  useEffect(() => {
    if (
      courseDates.length > 0 &&
      previousDateFormat !== null &&
      previousTimeFormat !== null &&
      (previousDateFormat !== dateFormat || previousTimeFormat !== timeFormat)
    ) {
      const convertedDates = convertCourseDatesFormat(
        courseDates,
        previousDateFormat,
        previousTimeFormat,
        dateFormat as DateFormat,
        timeFormat as TimeFormat
      );

      setCourseDates(convertedDates);
      formContext.setValue(courseFormFieldNames.courseDates, convertedDates);
    }

    setPreviousDateFormat(dateFormat as DateFormat);
    setPreviousTimeFormat(timeFormat as TimeFormat);
  }, [dateFormat, timeFormat, courseDates, formContext, previousDateFormat, previousTimeFormat]);

  const runCustomValidationAndSetErrors = (): {
    formValidationErrors: Record<string, string>;
    courseDateValidationErrors: Record<string, CourseDateError>;
  } => {
    const values = formContext.getValues();
    const currentCourseDates = courseDates.length > 0 ? courseDates : (values.courseDates ?? []);
    const formValidationErrors = validateCourseFormData(values);
    const courseDateValidationErrors = validateAllCourseDates(
      currentCourseDates,
      values.timezone ?? DEFAULT_TIMEZONE,
      dateFormat as DateFormat,
      timeFormat as TimeFormat
    );

    Object.keys(formValidationErrors).forEach(fieldName => {
      formContext.setError(fieldName as keyof CourseFormData, {
        type: 'manual',
        message: formValidationErrors[fieldName]
      });
    });

    setFormValidationErrors(formValidationErrors);
    setCourseDateErrors(courseDateValidationErrors);
    return { formValidationErrors, courseDateValidationErrors };
  };

  const handleSubmitAttempt = async () => {
    setHasAttemptedSubmit(true);

    const isFormValid = await formContext.trigger();

    const { formValidationErrors, courseDateValidationErrors } = runCustomValidationAndSetErrors();

    const hasCustomErrors =
      Object.keys(formValidationErrors).length > 0 ||
      Object.keys(courseDateValidationErrors).length > 0;

    if (!isFormValid || hasCustomErrors) {
      return;
    }

    formContext.handleSubmit(handleOnSubmit)();
  };

  handleSubmitAttemptRef.current = handleSubmitAttempt;

  const handleOnSubmit = (formData: CourseFormData) => {
    const currentCourseDates = courseDates.length > 0 ? courseDates : formData.courseDates || [];

    const courseDateValidationErrors = validateAllCourseDates(
      currentCourseDates,
      formData.timezone,
      dateFormat as DateFormat,
      timeFormat as TimeFormat
    );
    const formValidationErrors = validateCourseFormData(formData);

    setFormValidationErrors(formValidationErrors);
    setCourseDateErrors(courseDateValidationErrors);

    const hasCourseDateErrors = Object.keys(courseDateValidationErrors).length > 0;
    const hasFormErrors = Object.keys(formValidationErrors).length > 0;

    if (hasCourseDateErrors || hasFormErrors) {
      return;
    }

    const submissionData: CourseFormData = {
      ...formData,
      courseDates: currentCourseDates
    };

    setIsSubmitting(true);

    try {
      onSubmit(submissionData);
    } catch (error) {
      logger.warn('Course form submit error:', error);
      if (originalFormData) {
        formContext.reset(originalFormData);
      }
      toast.error(t('errors.genericError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    window.location.reload();
  };

  const handleFormKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.preventDefault();
      event.nativeEvent.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
    }
  };

  useEffect(() => {
    const container = formContainerRef.current;
    if (!container) {
      return;
    }

    let cleanup: (() => void) | void;
    const attach = () => {
      const form = container.querySelector('form');
      if (!form) {
        return;
      }
      const handleFormSubmit = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handleSubmitAttemptRef.current();
      };
      form.addEventListener('submit', handleFormSubmit, true);
      return () => {
        form.removeEventListener('submit', handleFormSubmit, true);
      };
    };

    const id = window.requestAnimationFrame(() => {
      cleanup = attach();
    });
    return () => {
      window.cancelAnimationFrame(id);
      cleanup?.();
    };
  }, [mode, formKey, courseId]);

  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Enter') {
        const target = event.target as HTMLElement;
        const formContainer = document.querySelector('[data-form-container]');
        if (formContainer?.contains(target)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true);
    };
  }, []);

  const handleSubmitButtonKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const isEdit = mode === FORM_MODES.EDIT;

  if (optionsError) {
    return <ErrorDisplay error={optionsError} onRetry={handleRetry} />;
  }

  if (isLoadingOptions || !canRenderForm) {
    return <LoadingSkeleton />;
  }

  if (!formOptions || (formOptions.timezones?.length ?? 0) === 0) {
    return (
      <StyledFormContainer>
        <div>{t('courseForm.optionsNotAvailable')}</div>
      </StyledFormContainer>
    );
  }

  const organizationOptions = mapOrganizationOptions(formOptions.organizations);
  const timezoneOptions = mapTimezoneOptions(formOptions.timezones);
  const datacenterOptions = mapDatacenterOptions(formOptions.datacenters);

  const renderedCourseDates = courseDates.map((courseDate, index) => (
    <CourseDateItem
      key={courseDate.id}
      courseDate={courseDate}
      index={index}
      totalDates={courseDates.length}
      userTimezone={userTimezone || DEFAULT_TIMEZONE}
      courseTimezone={currentTimezone}
      courseDateErrors={courseDateErrors}
      onDateRangeChange={updateCourseDateRange}
      onDateRangeBlur={validateDateRangeOnBlur}
      onRemoveDate={(id: string) => {
        openConfirm({
          content: t('courseForm.confirmRemoveDate'),
          onConfirm: () => {
            removeCourseDate(id);
          }
        });
      }}
      onAddDate={addCourseDate}
      getCourseDateForDisplay={getCourseDateForDisplay}
    />
  ));

  return (
    <div
      ref={formContainerRef}
      onKeyDown={handleFormKeyDown}
      onKeyDownCapture={handleFormKeyDown}
      data-form-container
      role='form'
      tabIndex={-1}
    >
      <FormContainer
        formContext={formContext}
        key={
          mode === FORM_MODES.EDIT && initialData && courseId
            ? `edit-${courseId}`
            : `create-${formKey}`
        }
      >
        <StyledFormContainer>
          <FormTextField
            name={courseFormFieldNames.title}
            label={t('courseForm.courseTitle')}
            required
            fullWidth
          />

          <AutocompleteField
            name={courseFormFieldNames.daOrganizationId}
            label={t('courseForm.organization')}
            options={organizationOptions}
            required
            fullWidth
            disabled={isEdit}
          />

          <Box
            component='fieldset'
            sx={{
              margin: 0,
              mb: 1,
              minWidth: 0,
              border: 1,
              borderColor: Colors.outlinedInputBorder,
              borderRadius: 1,
              p: 2,
              pt: 2
            }}
          >
            <Typography
              component='legend'
              variant='body2'
              sx={{
                margin: 0,
                px: 0.5,
                color: 'text.secondary',
                fontSize: '0.75rem'
              }}
            >
              {t('trainers.trainer')}
            </Typography>
            {(() => {
              const fromProps = initialData?.daOrganizationId ?? organizationIdForTrainers;
              const effectiveOrganizationId =
                fromProps != null && fromProps > 0
                  ? fromProps
                  : formOptions?.organizations?.[0]?.id;
              const allowImmediateInvite = isEdit && !!courseId;
              return organizationTrainers !== undefined &&
                organizationTrainersLoading !== undefined ? (
                <TrainerSelectSection
                  isEdit={isEdit}
                  courseId={courseId}
                  allowImmediateInvite={allowImmediateInvite}
                  initialOrganizationId={effectiveOrganizationId}
                  organizationTrainers={organizationTrainers}
                  organizationTrainersLoading={organizationTrainersLoading}
                  assignedTrainerFromCourse={assignedTrainerFromCourse}
                />
              ) : (
                <TrainerSelectSectionWithQuery
                  isEdit={isEdit}
                  courseId={courseId}
                  initialOrganizationId={effectiveOrganizationId}
                  assignedTrainerFromCourse={assignedTrainerFromCourse}
                />
              );
            })()}
          </Box>

          <ParticipantsSection
            hasAttemptedSubmit={hasAttemptedSubmit}
            formValidationErrors={formValidationErrors}
            datacenterOptions={datacenterOptions}
          />

          <AutocompleteField
            name={courseFormFieldNames.timezone}
            label={t('courseForm.timezone')}
            options={timezoneOptions}
            required
            fullWidth
            slotProps={{
              select: {
                MenuProps: {
                  style: { zIndex: 1600 },
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      zIndex: 1600
                    }
                  }
                }
              }
            }}
          />

          {renderedCourseDates}

          <FormTextField
            name={courseFormFieldNames.description}
            label={t('courseForm.description')}
            multiline
            minRows={2}
            maxRows={10}
            fullWidth
          />

          <CheckboxOptionsSection isEdit={isEdit} />

          {isEdit &&
            metadata &&
            (metadata.createdAt ??
              metadata.updatedAt ??
              metadata.createdBy ??
              metadata.updatedBy) && (
              <Box
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: `1px solid ${Colors.lineGrey}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5
                }}
              >
                {metadata.createdAt && (
                  <Typography
                    variant='caption'
                    sx={{
                      color: Colors.darkGrey,
                      fontSize: '11px',
                      lineHeight: '1.4'
                    }}
                  >
                    {t('courseForm.createdAt')}:{' '}
                    <DateInfo date={metadata.createdAt} useConfigFormat={true} />{' '}
                    <TimeInfo date={metadata.createdAt} useConfigFormat={true} />
                    {metadata.createdBy && ` ${t('courseForm.by')} ${metadata.createdBy}`}
                  </Typography>
                )}
                {metadata.updatedAt && (
                  <Typography
                    variant='caption'
                    sx={{
                      color: Colors.darkGrey,
                      fontSize: '11px',
                      lineHeight: '1.4'
                    }}
                  >
                    {t('courseForm.updatedAt')}:{' '}
                    <DateInfo date={metadata.updatedAt} useConfigFormat={true} />{' '}
                    <TimeInfo date={metadata.updatedAt} useConfigFormat={true} />
                    {metadata.updatedBy && ` ${t('courseForm.by')} ${metadata.updatedBy}`}
                  </Typography>
                )}
              </Box>
            )}

          <FormButtons
            onSubmit={handleSubmitAttempt}
            submitViaClickOnly
            onCancel={onCancel}
            submitText={t(isEdit ? 'courseForm.updateCourse' : 'courseForm.createCourse')}
            cancelText={t('courseForm.cancel')}
            isLoading={isSubmitting || (isLoading ?? false)}
            loadingText={t(isEdit ? 'courseForm.updating' : 'courseForm.creating')}
            submitButtonRef={submitButtonRef}
            onSubmitKeyDown={handleSubmitButtonKeyDown}
          />
        </StyledFormContainer>
      </FormContainer>

      <ConfirmationDialog
        open={isConfirmOpen}
        content={confirmContent}
        onNo={closeConfirm}
        onYes={handleConfirmYes}
      />
    </div>
  );
};
